import React, { useEffect, useState, useRef } from "react";
import { RetellWebClient } from "retell-client-js-sdk";

// Add Google Fonts import for Poppins
const poppinsFontLink = document.createElement('link');
poppinsFontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@600&display=swap';
poppinsFontLink.rel = 'stylesheet';
document.head.appendChild(poppinsFontLink);

const agentId = "agent_25d3edce4b87addb45fde0a93f";
const retellWebClient = new RetellWebClient();

interface UserDetails {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  zipCode: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  zipCode?: string;
}

const RiseWellTherapyButton = () => {
  const [isCalling, setIsCalling] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    zipCode: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const debounceTimeout = useRef<number | null>(null);

  // Force transparent background on all ancestors
  useEffect(() => {
    const originalBodyBg = document.body.style.background;
    const originalHtmlBg = document.documentElement.style.background;
    const root = document.getElementById('root');
    const originalRootBg = root ? root.style.background : '';
    const originalBodyMargin = document.body.style.margin;
    const originalHtmlMargin = document.documentElement.style.margin;
    const originalRootMargin = root ? root.style.margin : '';

    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    if (root) root.style.background = "transparent";
    document.body.style.margin = "0";
    document.documentElement.style.margin = "0";
    if (root) root.style.margin = "0";

    return () => {
      document.body.style.background = originalBodyBg;
      document.documentElement.style.background = originalHtmlBg;
      if (root) root.style.background = originalRootBg;
      document.body.style.margin = originalBodyMargin;
      document.documentElement.style.margin = originalHtmlMargin;
      if (root) root.style.margin = originalRootMargin;
    };
  }, []);

  // Inject CSS for error placeholder style
  useEffect(() => {
    const styleId = 'error-placeholder-style';
    if (document.getElementById(styleId)) return; // Don't add if already exists
    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.innerHTML = `
      .error-placeholder::placeholder { color: #e74c3c; opacity: 1; }
      .error-placeholder::-ms-input-placeholder { color: #e74c3c; }
    `;
    document.head.appendChild(styleElement);
  }, []);

  useEffect(() => {
    retellWebClient.on("call_started", () => setIsCalling(true));
    retellWebClient.on("call_ended", () => {
      setIsCalling(false);
      setIsLoading(false);
    });
    retellWebClient.on("error", () => {
      retellWebClient.stopCall();
      setIsCalling(false);
      setIsLoading(false);
    });
  }, []);

  // Form validation
  const validateForm = () => {
    const errors: FormErrors = {};
    
    if (!userDetails.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!userDetails.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!userDetails.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(userDetails.phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    if (!userDetails.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(userDetails.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!userDetails.zipCode.trim()) {
      errors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(userDetails.zipCode)) {
      errors.zipCode = 'Please enter a valid ZIP code';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field: keyof UserDetails, value: string) => {
    setUserDetails(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  async function registerCall(agentId: string, userDetails: UserDetails) {
    const response = await fetch("https://demo.braincx.ai:8080/create-web-call", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        agent_id: agentId,
        retell_llm_dynamic_variables: {
          first_name: userDetails.firstName,
          last_name: userDetails.lastName,
          phone: userDetails.phone,
          email: userDetails.email,
          zip_code: userDetails.zipCode
        }
      })
    });
    if (!response.ok) throw new Error(`Error: ${response.status}`);
    return await response.json();
  }

  // Debounce function to prevent rapid clicks
  const debounce = (func: Function, delay: number) => {
    return (...args: any[]) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = window.setTimeout(() => func(...args), delay);
    };
  };

  // Toggle conversation with debounce (matches ClassWallet/Gracehill)
  const toggleConversationDebounced = debounce(async () => {
    if (isLoading && !isCalling) return; // Prevent multiple calls while loading, but allow stop
    setIsLoading(true);

    if (isCalling) {
      retellWebClient.stopCall();
      setIsCalling(false);
      setIsLoading(false);
    } else {
      // Validate form before starting call
      if (!validateForm()) {
        setIsLoading(false);
        return;
      }

      try {
        const registerCallResponse = await registerCall(agentId, userDetails);
        if (registerCallResponse.access_token) {
          await retellWebClient.startCall({
            callId: registerCallResponse.call_id, accessToken: registerCallResponse.access_token, transport: registerCallResponse.transport, iceServers: registerCallResponse.ice_servers,
          });
          setIsCalling(true);
        }
      } catch (error) {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    }
  }, 300);

  return (
    <div style={{
          minHeight: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
        }}>
    <div style={{
            background: '#ffffff',
            height:'100vh',
            padding: '1rem',
            borderRadius: '12px',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '1.5rem',
            maxWidth: '450px',
            width: '100%',
            boxSizing: 'border-box',
            fontFamily: "'Poppins', sans-serif"
          }}>
            {/* Form Fields */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
    <div>
    <input
                  type="text"
                  placeholder={formErrors.firstName || "First Name"}
                  className={formErrors.firstName ? 'error-placeholder' : ''}
                  value={userDetails.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  style={{
                    width: '-webkit-fill-available',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: formErrors.firstName ? '2px solid #e74c3c' : '2px solid #ddd',
                    outline: 'none',
                    fontFamily: "'Poppins', sans-serif",
                    transition: 'border-color 0.2s'
                  }}
                />
    </div>
     
              <div>
    <input
                  type="text"
                  placeholder={formErrors.lastName || "Last Name"}
                  className={formErrors.lastName ? 'error-placeholder' : ''}
                  value={userDetails.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  style={{
                    width: '-webkit-fill-available',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: formErrors.lastName ? '2px solid #e74c3c' : '2px solid #ddd',
                    outline: 'none',
                    fontFamily: "'Poppins', sans-serif",
                    transition: 'border-color 0.2s'
                  }}
                />
    </div>
     
              <div>
    <input
                  type="tel"
                  placeholder={formErrors.phone || "Phone Number"}
                  className={formErrors.phone ? 'error-placeholder' : ''}
                  value={userDetails.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  style={{
                    width: '-webkit-fill-available',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: formErrors.phone ? '2px solid #e74c3c' : '2px solid #ddd',
                    outline: 'none',
                    fontFamily: "'Poppins', sans-serif",
                    transition: 'border-color 0.2s'
                  }}
                />
    </div>
     
              <div>
    <input
                  type="email"
                  placeholder={formErrors.email || "Email Address"}
                  className={formErrors.email ? 'error-placeholder' : ''}
                  value={userDetails.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  style={{
                    width: '-webkit-fill-available',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: formErrors.email ? '2px solid #e74c3c' : '2px solid #ddd',
                    outline: 'none',
                    fontFamily: "'Poppins', sans-serif",
                    transition: 'border-color 0.2s'
                  }}
                />
    </div>
     
              <div>
    <input
                  type="text"
                  placeholder={formErrors.zipCode || "ZIP Code"}
                  className={formErrors.zipCode ? 'error-placeholder' : ''}
                  value={userDetails.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  style={{
                    width: '-webkit-fill-available',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: formErrors.zipCode ? '2px solid #e74c3c' : '2px solid #ddd',
                    outline: 'none',
                    fontFamily: "'Poppins', sans-serif",
                    transition: 'border-color 0.2s'
                  }}
                />
    </div>
    </div>
     
            {/* Button */}
    <button
              style={{
                padding: "1rem 2rem",
                fontSize: "1rem",
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                borderRadius: "30px",
                border: "none",
                background: isCalling ? "#CE7123" : "#CE7123",
                color: "#fff",
                cursor: isLoading && !isCalling ? "not-allowed" : "pointer",
                outline: "none",
                transition: "background 0.2s, transform 0.2s",
                minWidth: "180px",
                letterSpacing: "1px"
              }}
              onClick={toggleConversationDebounced}
              disabled={isLoading && !isCalling}
    >
              {isCalling ? "Stop Demo" : "SCHEDULE TODAY"}
    </button>
    </div>
    </div>
      );
};

export default RiseWellTherapyButton;
